/**
 * App-framework chrome: the widgets and forms that wrap every page
 * (share, theme picker, bug reporter, contact form, page shell).
 *
 * Kept separate from messages.js so the per-page/auth copy there stays readable.
 * Placeholders use {name} and are substituted with fmt() from ./format.
 */

const en = {
  common: {
    cancel: 'Cancel',
    close: 'Close',
    sending: 'Sending...',
    submitting: 'Submitting...',
    loading: 'Loading...',
  },
  share: {
    label: 'Share',
    shareThisPage: 'Share this page',
    typeArticle: 'Article',
    typeTune: 'Tune',
    typeProduct: 'Product',
    shareType: 'Share {type}',
    shareThisType: 'Share this {type}',
    copyLink: 'Copy link',
    linkCopied: 'Link copied to clipboard',
    copyFailed: 'Could not copy link — please copy the URL below manually',
    signIn: 'Sign in',
    loginHint:
      'to email this page to someone. Use “Copy link” above to share the URL without signing in.',
    recipients: 'Send to email address(es)',
    recipientsPlaceholder: 'email@example.com, another@example.com',
    recipientsHelp: 'Enter one or more email addresses separated by commas',
    personalMessage: 'Personal Message (Optional)',
    personalMessagePlaceholder: 'Add a personal message to your recommendation...',
    charsUsed: '{used}/500 characters',
    errNoRecipients: 'Please enter at least one email address',
    errInvalidEmails: 'Invalid email address(es): {list}',
    sentPartial: 'Sent to {sent} recipient(s); {failed} failed.',
    sentAll: 'Shared successfully with {count} recipient(s)!',
    errShareFailed: 'Failed to share. Please try again.',
  },
  theme: {
    floatingLabel: 'Color',
    toggle: 'Toggle theme selector',
    closePanel: 'Close theme selector',
    change: 'Change theme',
    changeDrag: 'Change theme (drag to move)',
    choose: 'Choose Theme',
    chooseYours: 'Choose Your Theme',
    chooseHint: 'Select a color theme that matches your style',
    label: 'Theme:',
    selectNamed: 'Select {name} theme',
    active: 'Active',
  },
  bug: {
    floatingLabel: 'Report Bug',
    floatingTitle: 'Report a bug (drag to move)',
    floatingAria: 'Report a bug',
    title: 'Report a Bug',
    intro:
      "Help us improve! Please describe the issue you're experiencing. We'll capture technical details automatically to help diagnose the problem.",
    issueType: 'Issue Type',
    typeBug: 'Bug / Error',
    typeUi: 'UI / Display Issue',
    typePerformance: 'Performance Issue',
    typeFeature: 'Feature Request',
    typeOther: 'Other',
    issueTitle: 'Issue Title',
    issueTitlePlaceholder: 'Brief description of the issue',
    description: 'Detailed Description',
    descriptionPlaceholder:
      'Please describe what happened, what you expected to happen, and steps to reproduce the issue...',
    anonymous: 'Submit anonymously',
    anonymousNoteOn:
      'Without an email address, we cannot notify you when the issue is fixed or follow up with questions.',
    anonymousNoteOff:
      'Provide your email so we can update you on fixes and ask follow-up questions if needed. We use the same validation as account registration (real domain, not disposable addresses).',
    yourEmail: 'Your Email',
    emailPlaceholder: 'your.email@example.com',
    currentUrl: 'Current Page URL',
    openPopups: 'Open popup(s) when bug was reported:',
    diagnostics: 'Diagnostic Information (auto-captured):',
    browser: 'Browser',
    device: 'Device',
    screen: 'Screen',
    viewport: 'Viewport',
    loadingValue: 'Loading...',
    consoleCaptured: 'Console Logs Captured: {count} entries',
    errorsCount: '{count} errors',
    warningsCount: '{count} warnings',
    infoCount: '{count} info',
    logsCount: '{count} logs',
    noLogs:
      'No console logs captured yet. The system captures all console output from page load.',
    submit: 'Submit Bug Report',
    submitting: 'Submitting...',
    errEmailRequired: 'Email is required unless you submit anonymously.',
    errEmailInvalid: 'Please enter a valid email address.',
    toastSubmitting: 'Submitting bug report...',
    toastSuccess: 'Bug report submitted successfully! Thank you for helping us improve.',
    toastFailure:
      'Failed to submit bug report. Please try again or email directly to markus.van.kempen@gmail.com',
  },
  contact: {
    openForm: 'Send a message',
    title: 'Contact Us',
    intro: "Fill out the form below and we'll get back to you as soon as possible.",
    closeAria: 'Close contact form',
    successTitle: 'Thank you! Your message has been sent successfully.',
    successText: "We'll get back to you soon.",
    sendAnother: 'Send Another Message',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email',
    emailPlaceholder: 'your.email@example.com',
    inquiryType: 'Inquiry Type',
    inqGeneral: 'General Question',
    inqOrder: 'Order Inquiry',
    inqProduct: 'Product Information',
    inqWholesale: 'Wholesale Inquiry',
    inqTechnical: 'Technical Support',
    inqFeedback: 'Feedback',
    inqOther: 'Other',
    subject: 'Subject',
    subjectPlaceholder: 'Brief description of your inquiry',
    message: 'Message',
    messagePlaceholder: 'Please provide details about your inquiry, question, or feedback...',
    send: 'Send Message',
    errorPrefix: 'Error:',
    errSendFailed: 'Failed to send message. Please try again.',
    directEmail: 'Direct Email:',
    responseTime: 'Response Time:',
    responseTimeValue: 'Usually within 24 hours',
  },
  page: {
    loading: 'Loading page...',
    notFoundTitle: 'Page Not Found',
    notFoundText: "The page you're looking for doesn't exist or has been removed.",
    edit: 'Edit Page',
    editTitle: 'Edit this page',
    haveQuestions: 'Have Questions?',
    legalQuestions: "If you have any questions, please don't hesitate to reach out.",
    contactUs: 'Contact Us',
    emailLabel: 'Email:',
    phoneLabel: 'Phone:',
  },
  cookies: {
    aria: 'Cookie preferences',
    bannerLead: 'We use cookies',
    bannerRest:
      'to enhance your experience, analyze site usage, and assist in our marketing efforts. You can accept all, reject non-essential, or customize your preferences.',
    acceptAll: 'Accept All',
    rejectNonEssential: 'Reject Non-Essential',
    customize: 'Customize',
    title: 'Cookie Preferences',
    necessary: 'Strictly Necessary',
    necessaryAlways: '(always on)',
    necessaryDesc: 'Required for basic site functionality.',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how visitors interact with the site.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Used to deliver relevant ads and measure their effectiveness.',
    save: 'Save Preferences',
    back: 'Back',
  },
  abcTool: {
    seoTitle: 'MIDI to ABC Converter | Free Online ABC Tool',
    seoDescription:
      'Convert MIDI files to ABC notation online. Load, play, edit, and download Celtic fiddle tunes in ABC format — free tool from Cranford Publications.',
    iframeTitle: 'ABC Tool - MIDI to ABC Converter',
    loading: 'Loading ABC Tool...',
    loadError: 'Failed to load ABC Tool. Please try refreshing the page.',
    reload: 'Reload Page',
    pageTitle: 'MIDI to ABC Converter',
    title: 'MIDI to ABC Converter',
    subtitle: 'View, play, and convert fiddle tunes — starts with BarFly',
    jumpAria: 'Jump to sections',
    jumpTo: 'Jump to :',
    jumpAbc: 'ABC Text',
    jumpControls: 'Controls',
    jumpStatus: 'Status',
    jumpNotation: 'Standard Notation',
    howTo: 'How to Use',
    howPlay: '<strong>Play:</strong> Use Controls (right) for play, tempo, and instrument',
    howEdit: '<strong>Edit:</strong> Change ABC Text (left), then Update Notation',
    howUpload: '<strong>Upload:</strong> Use Upload in Controls for .mid / .midi / .abc files',
    howDownload: '<strong>Download:</strong> PDF, image, MIDI, or copy/save ABC from Controls',
    howLogin:
      '<strong>Note:</strong> <a href="/login">Log in</a> (or register) for full functionality — some download/save features require an account.',
    abcText: 'ABC Text',
    abcPlaceholder: 'ABC notation will appear here... You can also paste ABC text here directly.',
    updateNotation: '🔄 Update Notation from ABC Text',
    controls: 'Controls',
    tempo: 'Tempo (BPM):',
    instrument: 'Instrument:',
    pianoStandard: 'Piano - Standard (FluidR3_GM)',
    pianoHq: 'Piano - HQ Grand Piano (MusyngKite)',
    pianoSimple: 'Piano - Simple Synth',
    play: '▶ Play',
    stop: '⏹ Stop',
    copyAbc: '📋 Copy ABC',
    saveAbc: '💾 Save ABC',
    pdf: '📄 PDF',
    image: '🖼️ Image',
    midi: '🎵 MIDI',
    authMessage:
      'Please <a href="/login">log in</a> or register to use grayed out features.',
    uploadLabel: '📁 Upload MIDI or ABC File',
    uploadHint: 'Upload a .mid / .midi / .abc file to replace the example, or paste into ABC Text.',
    status: 'Status',
    statusLoadingExample: 'Loading example tune…',
    notation: 'Standard Notation',
    notationPlaceholder: 'Notation will appear here after conversion',
    loginToUse: 'Please log in or register to use this feature.',
    loginToUseFeature: 'Please log in or register to use the {feature} feature.',
    featSaveAbc: 'Save ABC',
    featPdf: 'Download PDF',
    featImage: 'Download Image',
    featMidi: 'Download MIDI',
    errLibraries: 'Error: Required libraries not available. Please refresh the page.',
    errNoAbc: 'No ABC text to render. Please paste or upload ABC notation.',
    rendering: 'Rendering notation from ABC text...',
    notationUpdated: '✓ Notation updated successfully!',
    warnSyntax: 'Warning: Could not render notation. Please check ABC syntax.',
    loadingBarfly: 'Loading example tune: BarFly...',
    dropFile: 'Please drop a MIDI (.mid, .midi) or ABC (.abc) file',
    selectFile: 'Please select a MIDI or ABC file',
    loadingAbc: 'Loading ABC file...',
    errConversionLibs: 'Error: Conversion libraries not loaded. Please refresh the page.',
    errConvertMidi: 'Error converting MIDI file. Please check the console for details.',
    loadingInstrument: 'Loading {name} ({font})...',
    noNotationPlay: 'No notation to play. Please load a file first.',
    playing: 'Playing...',
    stopped: 'Stopped.',
    errMidiPlayer: 'Error: MidiPlayer not loaded.',
    playbackFinished: 'Playback finished.',
    copied: '✓ Copied to clipboard!',
    saved: '✓ Saved!',
    noAbcSave: 'No ABC notation to save.',
    noNotationDownload: 'No notation to download.',
  },
  player: {
    title: 'Cranford Pub Music Player',
    openHint: 'Drag to move • Click to open',
    sampleHint: 'Drag to move • Click to hear a sample',
    signInHint: 'Sign in to listen to Cape Breton tunes',
    sampleBadge: 'Sample',
    guestBanner: 'This is a sample. Register free to hear {count} more Cape Breton tunes.',
    guestCtaRegister: 'Create an account',
    guestCtaSignIn: 'Sign in',
    guestCtaVerify: 'Verify email',
    guestLockedHint: 'Register to play the full list',
    verifyEmail:
      'Please verify your email to use the music player. Check your inbox or open your profile to resend verification.',
    dragToMove: 'Drag to move',
    offlineBadge: 'Offline · {count} cached tunes',
    offlineTooltip:
      '{count} tune MP3s are cached after an online visit (PWA install/update).',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    minimize: 'Minimize',
    tunePosition: 'Tune {current} of {total}',
    selectAria: 'Tune: {title}, track {current} of {total}. Open list to pick another.',
    seekHint: 'Tap or click to seek',
    previous: 'Previous',
    play: 'Play',
    pause: 'Pause',
    next: 'Next',
    shuffle: 'Shuffle',
    random: 'Play Random Track',
    volumeDown: 'Softer',
    volumeUp: 'Louder',
    volumeLabel: 'Volume {level} of 10',
  },
  nudge: {
    title: 'Unlock the Tune Player!',
    body: 'Access our interactive music player with playback controls',
    cta: 'Sign Up / Create Account',
  },
};

/** Canadian French says "courriel" where France says "e-mail". */
const french = (d) => ({
  common: {
    cancel: 'Annuler',
    close: 'Fermer',
    sending: 'Envoi...',
    submitting: 'Envoi...',
    loading: 'Chargement...',
  },
  share: {
    label: 'Partager',
    shareThisPage: 'Partager cette page',
    typeArticle: 'article',
    typeTune: 'air',
    typeProduct: 'produit',
    shareType: 'Partager cet {type}',
    shareThisType: 'Partager cet {type}',
    copyLink: 'Copier le lien',
    linkCopied: 'Lien copié dans le presse-papiers',
    copyFailed: 'Impossible de copier le lien — copiez l’adresse ci-dessous manuellement',
    signIn: 'Connectez-vous',
    loginHint: `pour envoyer cette page par ${d.emailNoun}. Utilisez « Copier le lien » ci-dessus pour partager l’adresse sans vous connecter.`,
    recipients: `Envoyer à une ou plusieurs adresses ${d.emailAdj}`,
    recipientsPlaceholder: 'adresse@exemple.com, autre@exemple.com',
    recipientsHelp: 'Saisissez une ou plusieurs adresses séparées par des virgules',
    personalMessage: 'Message personnel (facultatif)',
    personalMessagePlaceholder: 'Ajoutez un mot personnel à votre recommandation...',
    charsUsed: '{used}/500 caractères',
    errNoRecipients: 'Saisissez au moins une adresse',
    errInvalidEmails: 'Adresse(s) invalide(s) : {list}',
    sentPartial: 'Envoyé à {sent} destinataire(s) ; {failed} échec(s).',
    sentAll: 'Partagé avec {count} destinataire(s) !',
    errShareFailed: 'Le partage a échoué. Veuillez réessayer.',
  },
  theme: {
    floatingLabel: 'Couleurs',
    toggle: 'Afficher le sélecteur de thème',
    closePanel: 'Fermer le sélecteur de thème',
    change: 'Changer de thème',
    changeDrag: 'Changer de thème (glissez pour déplacer)',
    choose: 'Choisir un thème',
    chooseYours: 'Choisissez votre thème',
    chooseHint: 'Sélectionnez un thème de couleurs à votre goût',
    label: 'Thème :',
    selectNamed: 'Choisir le thème {name}',
    active: 'Actif',
  },
  bug: {
    floatingLabel: 'Signaler',
    floatingTitle: 'Signaler un problème (glissez pour déplacer)',
    floatingAria: 'Signaler un problème',
    title: 'Signaler un problème',
    intro:
      'Aidez-nous à nous améliorer ! Décrivez le problème rencontré. Nous recueillons automatiquement les détails techniques pour faciliter le diagnostic.',
    issueType: 'Type de problème',
    typeBug: 'Bogue / erreur',
    typeUi: 'Problème d’affichage',
    typePerformance: 'Problème de performance',
    typeFeature: 'Demande de fonctionnalité',
    typeOther: 'Autre',
    issueTitle: 'Titre du problème',
    issueTitlePlaceholder: 'Brève description du problème',
    description: 'Description détaillée',
    descriptionPlaceholder:
      'Décrivez ce qui s’est passé, ce que vous attendiez et les étapes pour reproduire le problème...',
    anonymous: 'Envoyer anonymement',
    anonymousNoteOn:
      'Sans adresse, nous ne pourrons pas vous prévenir de la correction ni vous poser de questions.',
    anonymousNoteOff: `Indiquez votre ${d.emailNoun} pour que nous puissions vous informer des correctifs et vous poser des questions au besoin. Nous utilisons la même validation qu’à l’inscription (domaine réel, pas d’adresse jetable).`,
    yourEmail: d.emailYours,
    emailPlaceholder: 'votre.adresse@exemple.com',
    currentUrl: 'Adresse de la page actuelle',
    openPopups: 'Fenêtre(s) ouverte(s) au moment du signalement :',
    diagnostics: 'Renseignements techniques (recueillis automatiquement) :',
    browser: 'Navigateur',
    device: 'Appareil',
    screen: 'Écran',
    viewport: 'Zone d’affichage',
    loadingValue: 'Chargement...',
    consoleCaptured: 'Journaux de console recueillis : {count} entrées',
    errorsCount: '{count} erreurs',
    warningsCount: '{count} avertissements',
    infoCount: '{count} infos',
    logsCount: '{count} journaux',
    noLogs:
      'Aucun journal de console pour le moment. Le système enregistre toutes les sorties depuis le chargement de la page.',
    submit: 'Envoyer le signalement',
    submitting: 'Envoi...',
    errEmailRequired: 'Une adresse est requise, sauf en envoi anonyme.',
    errEmailInvalid: 'Saisissez une adresse valide.',
    toastSubmitting: 'Envoi du signalement...',
    toastSuccess: 'Signalement envoyé. Merci de nous aider à améliorer le site !',
    toastFailure:
      'L’envoi du signalement a échoué. Réessayez ou écrivez directement à markus.van.kempen@gmail.com',
  },
  contact: {
    openForm: 'Envoyer un message',
    title: d.contactTitle,
    intro: 'Remplissez le formulaire ci-dessous et nous vous répondrons dès que possible.',
    closeAria: 'Fermer le formulaire',
    successTitle: 'Merci ! Votre message a bien été envoyé.',
    successText: 'Nous vous répondrons sous peu.',
    sendAnother: 'Envoyer un autre message',
    name: 'Nom',
    namePlaceholder: 'Votre nom complet',
    email: d.emailLabel,
    emailPlaceholder: 'votre.adresse@exemple.com',
    inquiryType: 'Type de demande',
    inqGeneral: 'Question générale',
    inqOrder: 'Question sur une commande',
    inqProduct: 'Renseignements sur un produit',
    inqWholesale: 'Demande de gros',
    inqTechnical: 'Soutien technique',
    inqFeedback: 'Commentaires',
    inqOther: 'Autre',
    subject: 'Objet',
    subjectPlaceholder: 'Brève description de votre demande',
    message: 'Message',
    messagePlaceholder: 'Précisez votre demande, votre question ou vos commentaires...',
    send: 'Envoyer le message',
    errorPrefix: 'Erreur :',
    errSendFailed: 'L’envoi du message a échoué. Veuillez réessayer.',
    directEmail: `${d.emailLabel} direct :`,
    responseTime: 'Délai de réponse :',
    responseTimeValue: 'Généralement sous 24 heures',
  },
  page: {
    loading: 'Chargement de la page...',
    notFoundTitle: 'Page introuvable',
    notFoundText: 'La page que vous cherchez n’existe pas ou a été retirée.',
    edit: 'Modifier la page',
    editTitle: 'Modifier cette page',
    haveQuestions: 'Des questions ?',
    legalQuestions: 'Si vous avez des questions, n’hésitez pas à nous écrire.',
    contactUs: d.contactTitle,
    emailLabel: `${d.emailLabel} :`,
    phoneLabel: 'Téléphone :',
  },
  cookies: {
    aria: 'Préférences de témoins',
    bannerLead: 'Nous utilisons des témoins',
    bannerRest:
      'pour améliorer votre expérience, analyser l’usage du site et soutenir nos efforts marketing. Vous pouvez tout accepter, refuser les témoins non essentiels ou personnaliser vos choix.',
    acceptAll: 'Tout accepter',
    rejectNonEssential: 'Refuser les non essentiels',
    customize: 'Personnaliser',
    title: 'Préférences de témoins',
    necessary: 'Strictement nécessaires',
    necessaryAlways: '(toujours actifs)',
    necessaryDesc: 'Requis pour le fonctionnement de base du site.',
    analytics: 'Témoins d’analyse',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs utilisent le site.',
    marketing: 'Témoins marketing',
    marketingDesc: 'Servent à diffuser des publicités pertinentes et à mesurer leur efficacité.',
    save: 'Enregistrer les préférences',
    back: 'Retour',
  },
  abcTool: {
    seoTitle: 'Convertisseur MIDI vers ABC | Outil ABC en ligne gratuit',
    seoDescription:
      'Convertissez des fichiers MIDI en notation ABC en ligne. Chargez, jouez, modifiez et téléchargez des airs de violon celtique au format ABC — outil gratuit de Cranford Publications.',
    iframeTitle: 'Outil ABC — convertisseur MIDI vers ABC',
    loading: 'Chargement de l’outil ABC...',
    loadError: 'Impossible de charger l’outil ABC. Actualisez la page.',
    reload: 'Recharger la page',
    pageTitle: 'Convertisseur MIDI vers ABC',
    title: 'Convertisseur MIDI vers ABC',
    subtitle: 'Affichez, jouez et convertissez des airs de violon — commence avec BarFly',
    jumpAria: 'Aller aux sections',
    jumpTo: 'Aller à :',
    jumpAbc: 'Texte ABC',
    jumpControls: 'Commandes',
    jumpStatus: 'État',
    jumpNotation: 'Notation classique',
    howTo: 'Mode d’emploi',
    howPlay: '<strong>Lecture :</strong> utilisez les commandes (à droite) pour jouer, le tempo et l’instrument',
    howEdit: '<strong>Modifier :</strong> changez le texte ABC (à gauche), puis mettez à jour la notation',
    howUpload: '<strong>Téléverser :</strong> utilisez Téléverser dans les commandes pour les fichiers .mid / .midi / .abc',
    howDownload: '<strong>Télécharger :</strong> PDF, image, MIDI, ou copier/enregistrer l’ABC depuis les commandes',
    howLogin:
      '<strong>Note :</strong> <a href="/login">connectez-vous</a> (ou créez un compte) pour toutes les fonctions — certains téléchargements et l’enregistrement exigent un compte.',
    abcText: 'Texte ABC',
    abcPlaceholder: 'La notation ABC apparaîtra ici... Vous pouvez aussi coller du texte ABC directement.',
    updateNotation: '🔄 Mettre à jour la notation depuis le texte ABC',
    controls: 'Commandes',
    tempo: 'Tempo (BPM) :',
    instrument: 'Instrument :',
    pianoStandard: 'Piano — standard (FluidR3_GM)',
    pianoHq: 'Piano — grand piano HQ (MusyngKite)',
    pianoSimple: 'Piano — synthé simple',
    play: '▶ Lecture',
    stop: '⏹ Arrêt',
    copyAbc: '📋 Copier l’ABC',
    saveAbc: '💾 Enregistrer l’ABC',
    pdf: '📄 PDF',
    image: '🖼️ Image',
    midi: '🎵 MIDI',
    authMessage:
      'Veuillez <a href="/login">vous connecter</a> ou créer un compte pour utiliser les fonctions grisées.',
    uploadLabel: '📁 Téléverser un fichier MIDI ou ABC',
    uploadHint: 'Téléversez un fichier .mid / .midi / .abc pour remplacer l’exemple, ou collez dans Texte ABC.',
    status: 'État',
    statusLoadingExample: 'Chargement de l’air d’exemple…',
    notation: 'Notation classique',
    notationPlaceholder: 'La notation apparaîtra ici après la conversion',
    loginToUse: 'Connectez-vous ou créez un compte pour utiliser cette fonction.',
    loginToUseFeature: 'Connectez-vous ou créez un compte pour utiliser {feature}.',
    featSaveAbc: 'Enregistrer l’ABC',
    featPdf: 'Télécharger le PDF',
    featImage: 'Télécharger l’image',
    featMidi: 'Télécharger le MIDI',
    errLibraries: 'Erreur : bibliothèques requises indisponibles. Actualisez la page.',
    errNoAbc: 'Aucun texte ABC à afficher. Collez ou téléversez de la notation ABC.',
    rendering: 'Rendu de la notation à partir du texte ABC...',
    notationUpdated: '✓ Notation mise à jour !',
    warnSyntax: 'Attention : impossible d’afficher la notation. Vérifiez la syntaxe ABC.',
    loadingBarfly: 'Chargement de l’air d’exemple : BarFly...',
    dropFile: 'Déposez un fichier MIDI (.mid, .midi) ou ABC (.abc)',
    selectFile: 'Sélectionnez un fichier MIDI ou ABC',
    loadingAbc: 'Chargement du fichier ABC...',
    errConversionLibs: 'Erreur : bibliothèques de conversion non chargées. Actualisez la page.',
    errConvertMidi: 'Erreur de conversion MIDI. Consultez la console pour les détails.',
    loadingInstrument: 'Chargement de {name} ({font})...',
    noNotationPlay: 'Aucune notation à jouer. Chargez d’abord un fichier.',
    playing: 'Lecture...',
    stopped: 'Arrêté.',
    errMidiPlayer: 'Erreur : MidiPlayer non chargé.',
    playbackFinished: 'Lecture terminée.',
    copied: '✓ Copié dans le presse-papiers !',
    saved: '✓ Enregistré !',
    noAbcSave: 'Aucune notation ABC à enregistrer.',
    noNotationDownload: 'Aucune notation à télécharger.',
  },
  player: {
    title: 'Lecteur Cranford Pub',
    openHint: 'Glissez pour déplacer • Cliquez pour ouvrir',
    sampleHint: 'Glissez pour déplacer • Cliquez pour écouter un extrait',
    signInHint: 'Connectez-vous pour écouter des airs du Cap-Breton',
    sampleBadge: 'Extrait',
    guestBanner: 'Ceci est un extrait. Créez un compte gratuit pour écouter {count} autres airs du Cap-Breton.',
    guestCtaRegister: 'Créer un compte',
    guestCtaSignIn: 'Connexion',
    guestCtaVerify: `Confirmer l’${d.emailNoun}`,
    guestLockedHint: 'Inscrivez-vous pour la liste complète',
    verifyEmail: `Veuillez confirmer votre ${d.emailNoun} pour utiliser le lecteur. Vérifiez votre boîte de réception ou ouvrez votre profil pour renvoyer la confirmation.`,
    dragToMove: 'Glissez pour déplacer',
    offlineBadge: 'Hors ligne · {count} airs en cache',
    offlineTooltip:
      '{count} fichiers MP3 sont mis en cache après une visite en ligne (installation ou mise à jour de l’appli).',
    fullscreen: 'Plein écran',
    exitFullscreen: 'Quitter le plein écran',
    minimize: 'Réduire',
    tunePosition: 'Air {current} sur {total}',
    selectAria:
      'Air : {title}, piste {current} sur {total}. Ouvrez la liste pour en choisir un autre.',
    seekHint: 'Touchez ou cliquez pour vous déplacer',
    previous: 'Précédent',
    play: 'Lecture',
    pause: 'Pause',
    next: 'Suivant',
    shuffle: 'Lecture aléatoire',
    random: 'Jouer un air au hasard',
    volumeDown: 'Moins fort',
    volumeUp: 'Plus fort',
    volumeLabel: 'Volume {level} sur 10',
  },
  nudge: {
    title: 'Débloquez le lecteur de musique !',
    body: 'Accédez à notre lecteur interactif avec les commandes de lecture',
    cta: 'Créer un compte',
  },
});

const de = {
  common: {
    cancel: 'Abbrechen',
    close: 'Schließen',
    sending: 'Wird gesendet...',
    submitting: 'Wird gesendet...',
    loading: 'Wird geladen...',
  },
  share: {
    label: 'Teilen',
    shareThisPage: 'Diese Seite teilen',
    typeArticle: 'Artikel',
    typeTune: 'Stück',
    typeProduct: 'Produkt',
    shareType: '{type} teilen',
    shareThisType: 'Dieses {type} teilen',
    copyLink: 'Link kopieren',
    linkCopied: 'Link in die Zwischenablage kopiert',
    copyFailed: 'Link konnte nicht kopiert werden — bitte die Adresse unten manuell kopieren',
    signIn: 'Melden Sie sich an',
    loginHint:
      'um diese Seite per E-Mail zu versenden. Mit „Link kopieren“ oben können Sie die Adresse auch ohne Anmeldung teilen.',
    recipients: 'An E-Mail-Adresse(n) senden',
    recipientsPlaceholder: 'adresse@beispiel.de, weitere@beispiel.de',
    recipientsHelp: 'Mehrere E-Mail-Adressen durch Komma trennen',
    personalMessage: 'Persönliche Nachricht (optional)',
    personalMessagePlaceholder: 'Fügen Sie Ihrer Empfehlung eine persönliche Nachricht hinzu...',
    charsUsed: '{used}/500 Zeichen',
    errNoRecipients: 'Bitte geben Sie mindestens eine E-Mail-Adresse ein',
    errInvalidEmails: 'Ungültige E-Mail-Adresse(n): {list}',
    sentPartial: 'An {sent} Empfänger gesendet; {failed} fehlgeschlagen.',
    sentAll: 'Erfolgreich mit {count} Empfänger(n) geteilt!',
    errShareFailed: 'Teilen fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },
  theme: {
    floatingLabel: 'Farben',
    toggle: 'Themenauswahl ein-/ausblenden',
    closePanel: 'Themenauswahl schließen',
    change: 'Design wechseln',
    changeDrag: 'Design wechseln (zum Verschieben ziehen)',
    choose: 'Design wählen',
    chooseYours: 'Wählen Sie Ihr Design',
    chooseHint: 'Wählen Sie ein Farbschema, das Ihnen gefällt',
    label: 'Design:',
    selectNamed: 'Design {name} auswählen',
    active: 'Aktiv',
  },
  bug: {
    floatingLabel: 'Fehler melden',
    floatingTitle: 'Fehler melden (zum Verschieben ziehen)',
    floatingAria: 'Fehler melden',
    title: 'Fehler melden',
    intro:
      'Helfen Sie uns, besser zu werden! Beschreiben Sie das Problem. Technische Details erfassen wir automatisch, um die Ursache zu finden.',
    issueType: 'Art des Problems',
    typeBug: 'Fehler',
    typeUi: 'Darstellungsproblem',
    typePerformance: 'Performance-Problem',
    typeFeature: 'Funktionswunsch',
    typeOther: 'Sonstiges',
    issueTitle: 'Titel',
    issueTitlePlaceholder: 'Kurze Beschreibung des Problems',
    description: 'Ausführliche Beschreibung',
    descriptionPlaceholder:
      'Beschreiben Sie, was passiert ist, was Sie erwartet haben und wie sich das Problem reproduzieren lässt...',
    anonymous: 'Anonym senden',
    anonymousNoteOn:
      'Ohne E-Mail-Adresse können wir Sie nicht über die Behebung informieren oder Rückfragen stellen.',
    anonymousNoteOff:
      'Geben Sie Ihre E-Mail-Adresse an, damit wir Sie über Korrekturen informieren und bei Bedarf nachfragen können. Wir prüfen sie wie bei der Registrierung (echte Domain, keine Wegwerfadresse).',
    yourEmail: 'Ihre E-Mail-Adresse',
    emailPlaceholder: 'ihre.adresse@beispiel.de',
    currentUrl: 'Adresse der aktuellen Seite',
    openPopups: 'Beim Melden geöffnete Fenster:',
    diagnostics: 'Technische Angaben (automatisch erfasst):',
    browser: 'Browser',
    device: 'Gerät',
    screen: 'Bildschirm',
    viewport: 'Anzeigebereich',
    loadingValue: 'Wird geladen...',
    consoleCaptured: 'Erfasste Konsolenmeldungen: {count} Einträge',
    errorsCount: '{count} Fehler',
    warningsCount: '{count} Warnungen',
    infoCount: '{count} Infos',
    logsCount: '{count} Meldungen',
    noLogs:
      'Noch keine Konsolenmeldungen erfasst. Das System protokolliert alle Ausgaben ab dem Laden der Seite.',
    submit: 'Fehlerbericht senden',
    submitting: 'Wird gesendet...',
    errEmailRequired: 'Eine E-Mail-Adresse ist erforderlich, außer bei anonymem Senden.',
    errEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    toastSubmitting: 'Fehlerbericht wird gesendet...',
    toastSuccess: 'Fehlerbericht gesendet. Danke, dass Sie uns helfen!',
    toastFailure:
      'Der Fehlerbericht konnte nicht gesendet werden. Bitte erneut versuchen oder direkt an markus.van.kempen@gmail.com schreiben',
  },
  contact: {
    openForm: 'Nachricht senden',
    title: 'Kontakt',
    intro: 'Füllen Sie das Formular aus — wir melden uns so schnell wie möglich.',
    closeAria: 'Kontaktformular schließen',
    successTitle: 'Danke! Ihre Nachricht wurde gesendet.',
    successText: 'Wir melden uns in Kürze bei Ihnen.',
    sendAnother: 'Weitere Nachricht senden',
    name: 'Name',
    namePlaceholder: 'Ihr vollständiger Name',
    email: 'E-Mail',
    emailPlaceholder: 'ihre.adresse@beispiel.de',
    inquiryType: 'Art der Anfrage',
    inqGeneral: 'Allgemeine Frage',
    inqOrder: 'Frage zu einer Bestellung',
    inqProduct: 'Produktinformation',
    inqWholesale: 'Händleranfrage',
    inqTechnical: 'Technischer Support',
    inqFeedback: 'Rückmeldung',
    inqOther: 'Sonstiges',
    subject: 'Betreff',
    subjectPlaceholder: 'Kurze Beschreibung Ihrer Anfrage',
    message: 'Nachricht',
    messagePlaceholder: 'Bitte schildern Sie Ihre Anfrage, Frage oder Rückmeldung...',
    send: 'Nachricht senden',
    errorPrefix: 'Fehler:',
    errSendFailed: 'Die Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.',
    directEmail: 'E-Mail direkt:',
    responseTime: 'Antwortzeit:',
    responseTimeValue: 'In der Regel innerhalb von 24 Stunden',
  },
  page: {
    loading: 'Seite wird geladen...',
    notFoundTitle: 'Seite nicht gefunden',
    notFoundText: 'Die gesuchte Seite existiert nicht oder wurde entfernt.',
    edit: 'Seite bearbeiten',
    editTitle: 'Diese Seite bearbeiten',
    haveQuestions: 'Noch Fragen?',
    legalQuestions: 'Wenn Sie Fragen haben, schreiben Sie uns gerne.',
    contactUs: 'Kontakt',
    emailLabel: 'E-Mail:',
    phoneLabel: 'Telefon:',
  },
  cookies: {
    aria: 'Cookie-Einstellungen',
    bannerLead: 'Wir verwenden Cookies',
    bannerRest:
      'um Ihr Erlebnis zu verbessern, die Nutzung der Website zu analysieren und unser Marketing zu unterstützen. Sie können alle akzeptieren, nicht notwendige ablehnen oder Ihre Auswahl anpassen.',
    acceptAll: 'Alle akzeptieren',
    rejectNonEssential: 'Nicht notwendige ablehnen',
    customize: 'Anpassen',
    title: 'Cookie-Einstellungen',
    necessary: 'Unbedingt erforderlich',
    necessaryAlways: '(immer aktiv)',
    necessaryDesc: 'Für die Grundfunktionen der Website erforderlich.',
    analytics: 'Analyse-Cookies',
    analyticsDesc: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.',
    marketing: 'Marketing-Cookies',
    marketingDesc: 'Dienen dazu, relevante Werbung auszuspielen und deren Wirkung zu messen.',
    save: 'Einstellungen speichern',
    back: 'Zurück',
  },
  abcTool: {
    seoTitle: 'MIDI-zu-ABC-Konverter | Kostenloses Online-ABC-Werkzeug',
    seoDescription:
      'MIDI-Dateien online in ABC-Notation umwandeln. Keltische Geigenstücke laden, abspielen, bearbeiten und herunterladen — kostenloses Werkzeug von Cranford Publications.',
    iframeTitle: 'ABC-Werkzeug — MIDI-zu-ABC-Konverter',
    loading: 'ABC-Werkzeug wird geladen...',
    loadError: 'ABC-Werkzeug konnte nicht geladen werden. Bitte Seite aktualisieren.',
    reload: 'Seite neu laden',
    pageTitle: 'MIDI-zu-ABC-Konverter',
    title: 'MIDI-zu-ABC-Konverter',
    subtitle: 'Stücke anzeigen, abspielen und umwandeln — startet mit BarFly',
    jumpAria: 'Zu Abschnitten springen',
    jumpTo: 'Springen zu :',
    jumpAbc: 'ABC-Text',
    jumpControls: 'Steuerung',
    jumpStatus: 'Status',
    jumpNotation: 'Klassische Notation',
    howTo: 'So geht’s',
    howPlay: '<strong>Abspielen:</strong> Steuerung (rechts) für Wiedergabe, Tempo und Instrument',
    howEdit: '<strong>Bearbeiten:</strong> ABC-Text (links) ändern, dann Notation aktualisieren',
    howUpload: '<strong>Hochladen:</strong> Hochladen in der Steuerung für .mid / .midi / .abc',
    howDownload: '<strong>Herunterladen:</strong> PDF, Bild, MIDI oder ABC kopieren/speichern',
    howLogin:
      '<strong>Hinweis:</strong> <a href="/login">Anmelden</a> (oder registrieren) für alle Funktionen — einige Downloads und Speichern erfordern ein Konto.',
    abcText: 'ABC-Text',
    abcPlaceholder: 'ABC-Notation erscheint hier... Sie können ABC-Text auch direkt einfügen.',
    updateNotation: '🔄 Notation aus ABC-Text aktualisieren',
    controls: 'Steuerung',
    tempo: 'Tempo (BPM):',
    instrument: 'Instrument:',
    pianoStandard: 'Klavier — Standard (FluidR3_GM)',
    pianoHq: 'Klavier — HQ-Flügel (MusyngKite)',
    pianoSimple: 'Klavier — einfacher Synth',
    play: '▶ Abspielen',
    stop: '⏹ Stopp',
    copyAbc: '📋 ABC kopieren',
    saveAbc: '💾 ABC speichern',
    pdf: '📄 PDF',
    image: '🖼️ Bild',
    midi: '🎵 MIDI',
    authMessage:
      'Bitte <a href="/login">anmelden</a> oder registrieren, um die ausgegrauten Funktionen zu nutzen.',
    uploadLabel: '📁 MIDI- oder ABC-Datei hochladen',
    uploadHint: 'Laden Sie eine .mid- / .midi- / .abc-Datei hoch oder fügen Sie Text unter ABC-Text ein.',
    status: 'Status',
    statusLoadingExample: 'Beispielstück wird geladen…',
    notation: 'Klassische Notation',
    notationPlaceholder: 'Die Notation erscheint nach der Umwandlung',
    loginToUse: 'Bitte anmelden oder registrieren, um diese Funktion zu nutzen.',
    loginToUseFeature: 'Bitte anmelden oder registrieren, um {feature} zu nutzen.',
    featSaveAbc: 'ABC speichern',
    featPdf: 'PDF herunterladen',
    featImage: 'Bild herunterladen',
    featMidi: 'MIDI herunterladen',
    errLibraries: 'Fehler: Erforderliche Bibliotheken nicht verfügbar. Bitte Seite aktualisieren.',
    errNoAbc: 'Kein ABC-Text zum Anzeigen. Bitte ABC-Notation einfügen oder hochladen.',
    rendering: 'Notation wird aus ABC-Text erzeugt...',
    notationUpdated: '✓ Notation aktualisiert!',
    warnSyntax: 'Hinweis: Notation konnte nicht erzeugt werden. Bitte ABC-Syntax prüfen.',
    loadingBarfly: 'Beispielstück wird geladen: BarFly...',
    dropFile: 'Bitte eine MIDI- (.mid, .midi) oder ABC-Datei (.abc) ablegen',
    selectFile: 'Bitte eine MIDI- oder ABC-Datei wählen',
    loadingAbc: 'ABC-Datei wird geladen...',
    errConversionLibs: 'Fehler: Umwandlungsbibliotheken nicht geladen. Bitte Seite aktualisieren.',
    errConvertMidi: 'Fehler beim Umwandeln der MIDI-Datei. Details in der Konsole.',
    loadingInstrument: '{name} ({font}) wird geladen...',
    noNotationPlay: 'Keine Notation zum Abspielen. Bitte zuerst eine Datei laden.',
    playing: 'Wiedergabe...',
    stopped: 'Gestoppt.',
    errMidiPlayer: 'Fehler: MidiPlayer nicht geladen.',
    playbackFinished: 'Wiedergabe beendet.',
    copied: '✓ In die Zwischenablage kopiert!',
    saved: '✓ Gespeichert!',
    noAbcSave: 'Keine ABC-Notation zum Speichern.',
    noNotationDownload: 'Keine Notation zum Herunterladen.',
  },
  player: {
    title: 'Cranford Pub Musikplayer',
    openHint: 'Ziehen zum Verschieben • Klicken zum Öffnen',
    sampleHint: 'Ziehen zum Verschieben • Klicken für eine Probe',
    signInHint: 'Melden Sie sich an, um Stücke aus Cape Breton zu hören',
    sampleBadge: 'Probe',
    guestBanner: 'Das ist eine Probe. Registrieren Sie sich kostenlos, um {count} weitere Stücke aus Cape Breton zu hören.',
    guestCtaRegister: 'Konto erstellen',
    guestCtaSignIn: 'Anmelden',
    guestCtaVerify: 'E-Mail bestätigen',
    guestLockedHint: 'Registrieren, um die volle Liste zu hören',
    verifyEmail:
      'Bitte bestätigen Sie Ihre E-Mail-Adresse, um den Musikplayer zu nutzen. Prüfen Sie Ihr Postfach oder öffnen Sie Ihr Profil, um die Bestätigung erneut zu senden.',
    dragToMove: 'Ziehen zum Verschieben',
    offlineBadge: 'Offline · {count} Stücke im Cache',
    offlineTooltip:
      '{count} MP3-Dateien werden nach einem Online-Besuch zwischengespeichert (App-Installation oder -Update).',
    fullscreen: 'Vollbild',
    exitFullscreen: 'Vollbild beenden',
    minimize: 'Minimieren',
    tunePosition: 'Stück {current} von {total}',
    selectAria:
      'Stück: {title}, Titel {current} von {total}. Liste öffnen, um ein anderes zu wählen.',
    seekHint: 'Tippen oder klicken zum Spulen',
    previous: 'Zurück',
    play: 'Abspielen',
    pause: 'Pause',
    next: 'Weiter',
    shuffle: 'Zufallswiedergabe',
    random: 'Zufälliges Stück abspielen',
    volumeDown: 'Leiser',
    volumeUp: 'Lauter',
    volumeLabel: 'Lautstärke {level} von 10',
  },
  nudge: {
    title: 'Musikplayer freischalten!',
    body: 'Nutzen Sie unseren interaktiven Player mit Wiedergabesteuerung',
    cta: 'Konto erstellen',
  },
};

const ga = {
  common: {
    cancel: 'Cealaigh',
    close: 'Dún',
    sending: 'Á sheoladh...',
    submitting: 'Á sheoladh...',
    loading: 'Á lódáil...',
  },
  share: {
    label: 'Roinn',
    shareThisPage: 'Roinn an leathanach seo',
    typeArticle: 'alt',
    typeTune: 'port',
    typeProduct: 'táirge',
    shareType: 'Roinn an {type}',
    shareThisType: 'Roinn an {type} seo',
    copyLink: 'Cóipeáil an nasc',
    linkCopied: 'Cóipeáladh an nasc',
    copyFailed: 'Níorbh fhéidir an nasc a chóipeáil — cóipeáil an URL thíos de láimh',
    signIn: 'Logáil isteach',
    loginHint:
      'chun an leathanach seo a sheoladh ar ríomhphost. Úsáid “Cóipeáil an nasc” thuas chun an URL a roinnt gan logáil isteach.',
    recipients: 'Seol chuig seoladh/seoltaí ríomhphoist',
    recipientsPlaceholder: 'seoladh@sampla.ie, ceann.eile@sampla.ie',
    recipientsHelp: 'Cuir isteach seoladh amháin nó níos mó, scartha le camóga',
    personalMessage: 'Teachtaireacht phearsanta (roghnach)',
    personalMessagePlaceholder: 'Cuir nóta pearsanta le do mholadh...',
    charsUsed: '{used}/500 carachtar',
    errNoRecipients: 'Cuir isteach seoladh ríomhphoist amháin ar a laghad',
    errInvalidEmails: 'Seoladh/seoltaí neamhbhailí: {list}',
    sentPartial: 'Seoladh chuig {sent} faighteoir; theip ar {failed}.',
    sentAll: 'Roinnte le {count} faighteoir!',
    errShareFailed: 'Theip ar an roinnt. Bain triail eile as.',
  },
  theme: {
    floatingLabel: 'Dathanna',
    toggle: 'Taispeáin roghnóir na dtéamaí',
    closePanel: 'Dún roghnóir na dtéamaí',
    change: 'Athraigh an téama',
    changeDrag: 'Athraigh an téama (tarraing chun bogadh)',
    choose: 'Roghnaigh téama',
    chooseYours: 'Roghnaigh do théama',
    chooseHint: 'Roghnaigh scéim dathanna a oireann duit',
    label: 'Téama:',
    selectNamed: 'Roghnaigh an téama {name}',
    active: 'Gníomhach',
  },
  bug: {
    floatingLabel: 'Tuairiscigh',
    floatingTitle: 'Tuairiscigh fabht (tarraing chun bogadh)',
    floatingAria: 'Tuairiscigh fabht',
    title: 'Tuairiscigh fabht',
    intro:
      'Cabhraigh linn feabhas a chur ar an suíomh! Déan cur síos ar an bhfadhb. Baileoimid na sonraí teicniúla go huathoibríoch chun cabhrú leis an bhfadhb a aimsiú.',
    issueType: 'Cineál na faidhbe',
    typeBug: 'Fabht / earráid',
    typeUi: 'Fadhb taispeána',
    typePerformance: 'Fadhb feidhmíochta',
    typeFeature: 'Iarratas ar ghné',
    typeOther: 'Eile',
    issueTitle: 'Teideal',
    issueTitlePlaceholder: 'Cur síos gairid ar an bhfadhb',
    description: 'Cur síos mionsonraithe',
    descriptionPlaceholder:
      'Déan cur síos ar a tharla, ar a raibh tú ag súil leis, agus ar na céimeanna chun an fhadhb a atháirgeadh...',
    anonymous: 'Seol gan ainm',
    anonymousNoteOn:
      'Gan seoladh ríomhphoist, ní féidir linn a chur in iúl duit nuair a bheidh an fhadhb réitithe ná ceisteanna a chur ort.',
    anonymousNoteOff:
      'Tabhair dúinn do sheoladh ríomhphoist chun go bhféadfaimis eolas a thabhairt duit faoi réitigh agus ceisteanna a chur más gá. Bainimid úsáid as an bhfíorú céanna atá ag an gclárú (fíorfhearann, gan seoltaí indiúscartha).',
    yourEmail: 'Do ríomhphost',
    emailPlaceholder: 'do.sheoladh@sampla.ie',
    currentUrl: 'URL an leathanaigh reatha',
    openPopups: 'Fuinneog(a) a bhí ar oscailt ag am na tuairisce:',
    diagnostics: 'Eolas teicniúil (bailithe go huathoibríoch):',
    browser: 'Brabhsálaí',
    device: 'Gléas',
    screen: 'Scáileán',
    viewport: 'Amharcphort',
    loadingValue: 'Á lódáil...',
    consoleCaptured: 'Logaí consóil bailithe: {count} iontráil',
    errorsCount: '{count} earráid',
    warningsCount: '{count} rabhadh',
    infoCount: '{count} eolas',
    logsCount: '{count} log',
    noLogs:
      'Níl aon log consóil bailithe go fóill. Bailíonn an córas gach aschur ón uair a lódáiltear an leathanach.',
    submit: 'Seol an tuairisc',
    submitting: 'Á sheoladh...',
    errEmailRequired: 'Tá seoladh ríomhphoist riachtanach mura seolann tú gan ainm.',
    errEmailInvalid: 'Cuir isteach seoladh ríomhphoist bailí.',
    toastSubmitting: 'Tuairisc á seoladh...',
    toastSuccess: 'Seoladh an tuairisc. Go raibh maith agat as cabhrú linn!',
    toastFailure:
      'Theip ar sheoladh na tuairisce. Bain triail eile as nó scríobh go díreach chuig markus.van.kempen@gmail.com',
  },
  contact: {
    openForm: 'Seol teachtaireacht',
    title: 'Déan teagmháil linn',
    intro: 'Líon isteach an fhoirm thíos agus freagróimid chomh luath agus is féidir.',
    closeAria: 'Dún an fhoirm teagmhála',
    successTitle: 'Go raibh maith agat! Seoladh do theachtaireacht.',
    successText: 'Beimid i dteagmháil leat go luath.',
    sendAnother: 'Seol teachtaireacht eile',
    name: 'Ainm',
    namePlaceholder: 'Do ainm iomlán',
    email: 'Ríomhphost',
    emailPlaceholder: 'do.sheoladh@sampla.ie',
    inquiryType: 'Cineál ceiste',
    inqGeneral: 'Ceist ghinearálta',
    inqOrder: 'Ceist faoi ordú',
    inqProduct: 'Eolas faoi tháirge',
    inqWholesale: 'Ceist mhórdhíola',
    inqTechnical: 'Tacaíocht teicniúil',
    inqFeedback: 'Aiseolas',
    inqOther: 'Eile',
    subject: 'Ábhar',
    subjectPlaceholder: 'Cur síos gairid ar do cheist',
    message: 'Teachtaireacht',
    messagePlaceholder: 'Tabhair sonraí faoi do cheist nó faoi do aiseolas...',
    send: 'Seol an teachtaireacht',
    errorPrefix: 'Earráid:',
    errSendFailed: 'Theip ar sheoladh na teachtaireachta. Bain triail eile as.',
    directEmail: 'Ríomhphost díreach:',
    responseTime: 'Am freagra:',
    responseTimeValue: 'Faoi 24 uair an chloig de ghnáth',
  },
  page: {
    loading: 'Leathanach á lódáil...',
    notFoundTitle: 'Leathanach gan aimsiú',
    notFoundText: 'Níl an leathanach atá tú a lorg ann nó baineadh anuas é.',
    edit: 'Cuir an leathanach in eagar',
    editTitle: 'Cuir an leathanach seo in eagar',
    haveQuestions: 'Ceisteanna agat?',
    legalQuestions: 'Má tá aon cheist agat, ná bíodh drogall ort scríobh chugainn.',
    contactUs: 'Déan teagmháil linn',
    emailLabel: 'Ríomhphost:',
    phoneLabel: 'Teileafón:',
  },
  cookies: {
    aria: 'Sainroghanna fianán',
    bannerLead: 'Úsáidimid fianáin',
    bannerRest:
      'chun d’eispéireas a fheabhsú, úsáid an tsuímh a anailísiú, agus cabhrú lenár margaíocht. Is féidir leat gach ceann a ghlacadh, cinn neamhriachtanacha a dhiúltú, nó do roghanna a shaincheapadh.',
    acceptAll: 'Glac le gach ceann',
    rejectNonEssential: 'Diúltaigh do na cinn neamhriachtanacha',
    customize: 'Saincheap',
    title: 'Sainroghanna fianán',
    necessary: 'Riachtanach go docht',
    necessaryAlways: '(i gcónaí ar siúl)',
    necessaryDesc: 'Ag teastáil le haghaidh feidhmiú bunúsach an tsuímh.',
    analytics: 'Fianáin anailíse',
    analyticsDesc: 'Cabhraíonn siad linn tuiscint a fháil ar an gcaoi a n-úsáideann cuairteoirí an suíomh.',
    marketing: 'Fianáin mhargaíochta',
    marketingDesc: 'Úsáidtear iad chun fógraí ábhartha a sheachadadh agus a n-éifeacht a thomhas.',
    save: 'Sábháil na sainroghanna',
    back: 'Ar ais',
  },
  abcTool: {
    seoTitle: 'Tiontaire MIDI go ABC | Uirlis ABC saor in aisce',
    seoDescription:
      'Tiontaigh comhaid MIDI go nodaireacht ABC ar líne. Luchtaigh, seinn, cuir in eagar agus íoslódáil poirt veidhlín Cheilteacha i bhformáid ABC.',
    iframeTitle: 'Uirlis ABC — tiontaire MIDI go ABC',
    loading: 'Uirlis ABC á lódáil...',
    loadError: 'Níorbh fhéidir an uirlis ABC a lódáil. Athnuaigh an leathanach.',
    reload: 'Athlódáil an leathanach',
    pageTitle: 'Tiontaire MIDI go ABC',
    title: 'Tiontaire MIDI go ABC',
    subtitle: 'Féach, seinn agus tiontaigh poirt veidhlín — tosaíonn le BarFly',
    jumpAria: 'Léim chuig rannóga',
    jumpTo: 'Léim chuig :',
    jumpAbc: 'Téacs ABC',
    jumpControls: 'Rialtáin',
    jumpStatus: 'Stádas',
    jumpNotation: 'Nodaireacht chlasaiceach',
    howTo: 'Conas é a úsáid',
    howPlay: '<strong>Seinn:</strong> úsáid na rialtáin (ar dheis) le haghaidh seinnte, luas agus uirlis',
    howEdit: '<strong>Cuir in eagar:</strong> athraigh an téacs ABC (ar chlé), ansin nuashonraigh an nodaireacht',
    howUpload: '<strong>Uaslódáil:</strong> úsáid Uaslódáil sna rialtáin le haghaidh .mid / .midi / .abc',
    howDownload: '<strong>Íoslódáil:</strong> PDF, íomhá, MIDI, nó cóipeáil/sábháil ABC ó na rialtáin',
    howLogin:
      '<strong>Nóta:</strong> <a href="/login">logáil isteach</a> (nó cláraigh) le haghaidh gach feidhm — teastaíonn cuntas le haghaidh roinnt íoslódálacha.',
    abcText: 'Téacs ABC',
    abcPlaceholder: 'Beidh nodaireacht ABC anseo... Is féidir téacs ABC a ghreamú anseo freisin.',
    updateNotation: '🔄 Nuashonraigh an nodaireacht ón téacs ABC',
    controls: 'Rialtáin',
    tempo: 'Luas (BPM):',
    instrument: 'Uirlis:',
    pianoStandard: 'Pianó — caighdeánach (FluidR3_GM)',
    pianoHq: 'Pianó — grand HQ (MusyngKite)',
    pianoSimple: 'Pianó — sintéis shimplí',
    play: '▶ Seinn',
    stop: '⏹ Stop',
    copyAbc: '📋 Cóipeáil ABC',
    saveAbc: '💾 Sábháil ABC',
    pdf: '📄 PDF',
    image: '🖼️ Íomhá',
    midi: '🎵 MIDI',
    authMessage:
      '<a href="/login">Logáil isteach</a> nó cláraigh chun na gnéithe liathaithe a úsáid.',
    uploadLabel: '📁 Uaslódáil comhad MIDI nó ABC',
    uploadHint: 'Uaslódáil .mid / .midi / .abc in ionad an tsampla, nó greamaigh sa Téacs ABC.',
    status: 'Stádas',
    statusLoadingExample: 'Port samplach á lódáil…',
    notation: 'Nodaireacht chlasaiceach',
    notationPlaceholder: 'Beidh an nodaireacht anseo tar éis an tiontaithe',
    loginToUse: 'Logáil isteach nó cláraigh chun an ghné seo a úsáid.',
    loginToUseFeature: 'Logáil isteach nó cláraigh chun {feature} a úsáid.',
    featSaveAbc: 'Sábháil ABC',
    featPdf: 'Íoslódáil PDF',
    featImage: 'Íoslódáil íomhá',
    featMidi: 'Íoslódáil MIDI',
    errLibraries: 'Earráid: níl na leabharlanna riachtanacha ar fáil. Athnuaigh an leathanach.',
    errNoAbc: 'Níl téacs ABC le taispeáint. Greamaigh nó uaslódáil nodaireacht ABC.',
    rendering: 'Nodaireacht á rindreáil ón téacs ABC...',
    notationUpdated: '✓ Nodaireacht nuashonraithe!',
    warnSyntax: 'Rabhadh: níorbh fhéidir an nodaireacht a rindreáil. Seiceáil an chomhréir ABC.',
    loadingBarfly: 'Port samplach á lódáil: BarFly...',
    dropFile: 'Scaoil comhad MIDI (.mid, .midi) nó ABC (.abc)',
    selectFile: 'Roghnaigh comhad MIDI nó ABC',
    loadingAbc: 'Comhad ABC á lódáil...',
    errConversionLibs: 'Earráid: níl na leabharlanna tiontaithe lódáilte. Athnuaigh an leathanach.',
    errConvertMidi: 'Earráid ag tiontú an chomhaid MIDI. Féach an consól.',
    loadingInstrument: '{name} ({font}) á lódáil...',
    noNotationPlay: 'Níl nodaireacht le seinm. Luchtaigh comhad ar dtús.',
    playing: 'Á sheinm...',
    stopped: 'Stoptha.',
    errMidiPlayer: 'Earráid: níl MidiPlayer lódáilte.',
    playbackFinished: 'Críochnaíodh an seinn.',
    copied: '✓ Cóipeáilte chuig an ngearrthaisce!',
    saved: '✓ Sábháilte!',
    noAbcSave: 'Níl nodaireacht ABC le sábháil.',
    noNotationDownload: 'Níl nodaireacht le híoslódáil.',
  },
  player: {
    title: 'Seinnteoir Cranford Pub',
    openHint: 'Tarraing chun bogadh • Cliceáil chun oscailt',
    sampleHint: 'Tarraing chun bogadh • Cliceáil chun sampla a chloisteáil',
    signInHint: 'Logáil isteach chun poirt ó Cheap Breatainn a chloisteáil',
    sampleBadge: 'Sampla',
    guestBanner: 'Sampla é seo. Cláraigh saor in aisce chun {count} port eile ó Cheap Breatainn a chloisteáil.',
    guestCtaRegister: 'Cruthaigh cuntas',
    guestCtaSignIn: 'Logáil isteach',
    guestCtaVerify: 'Deimhnigh ríomhphost',
    guestLockedHint: 'Cláraigh chun an liosta iomlán a sheinm',
    verifyEmail:
      'Deimhnigh do ríomhphost chun an seinnteoir a úsáid. Seiceáil do bhosca isteach nó oscail do phróifíl chun an nasc deimhnithe a sheoladh arís.',
    dragToMove: 'Tarraing chun bogadh',
    offlineBadge: 'As líne · {count} port i dtaisce',
    offlineTooltip:
      'Cuirtear {count} comhad MP3 i dtaisce tar éis cuairt ar líne (suiteáil nó nuashonrú na haipe).',
    fullscreen: 'Lánscáileán',
    exitFullscreen: 'Fág an lánscáileán',
    minimize: 'Íoslaghdaigh',
    tunePosition: 'Port {current} as {total}',
    selectAria: 'Port: {title}, rian {current} as {total}. Oscail an liosta chun ceann eile a roghnú.',
    seekHint: 'Tapáil nó cliceáil chun bogadh',
    previous: 'Roimhe',
    play: 'Seinn',
    pause: 'Cuir ar sos',
    next: 'Ar aghaidh',
    shuffle: 'Suaitheadh',
    random: 'Seinn port randamach',
    volumeDown: 'Níos ciúine',
    volumeUp: 'Níos airde',
    volumeLabel: 'Airde {level} as 10',
  },
  nudge: {
    title: 'Díghlasáil an seinnteoir ceoil!',
    body: 'Faigh rochtain ar ár seinnteoir idirghníomhach le rialtáin sheinnte',
    cta: 'Cruthaigh cuntas',
  },
};

const gd = {
  common: {
    cancel: 'Sguir dheth',
    close: 'Dùin',
    sending: 'A’ cur...',
    submitting: 'A’ cur...',
    loading: 'A’ luchdadh...',
  },
  share: {
    label: 'Co-roinn',
    shareThisPage: 'Co-roinn an duilleag seo',
    typeArticle: 'aiste',
    typeTune: 'port',
    typeProduct: 'bathar',
    shareType: 'Co-roinn an {type}',
    shareThisType: 'Co-roinn an {type} seo',
    copyLink: 'Dèan lethbhreac dhen cheangal',
    linkCopied: 'Chaidh an ceangal a chopaigeadh',
    copyFailed:
      'Cha b’ urrainn dhuinn an ceangal a chopaigeadh — dèan lethbhreac dhen URL gu h-ìosal le làimh',
    signIn: 'Log a-steach',
    loginHint:
      'airson an duilleag seo a chur air a’ phost-d. Cleachd “Dèan lethbhreac dhen cheangal” gu h-àrd airson an URL a cho-roinn gun a bhith a’ logadh a-steach.',
    recipients: 'Cuir gu seòladh(an) puist-d',
    recipientsPlaceholder: 'seoladh@ball-sampaill.scot, fear.eile@ball-sampaill.scot',
    recipientsHelp: 'Cuir a-steach seòladh no dhà, air an sgaradh le cromagan',
    personalMessage: 'Teachdaireachd phearsanta (roghainneil)',
    personalMessagePlaceholder: 'Cuir nòta pearsanta ris a’ mholadh agad...',
    charsUsed: '{used}/500 caractar',
    errNoRecipients: 'Cuir a-steach co-dhiù aon seòladh puist-d',
    errInvalidEmails: 'Seòladh(an) mì-dhligheach: {list}',
    sentPartial: 'Air a chur gu {sent} faightear; dh’fhàillig {failed}.',
    sentAll: 'Air a cho-roinn le {count} faightear!',
    errShareFailed: 'Dh’fhàillig an co-roinn. Feuch ris a-rithist.',
  },
  theme: {
    floatingLabel: 'Dathan',
    toggle: 'Seall roghnaichear nan ùrlar',
    closePanel: 'Dùin roghnaichear nan ùrlar',
    change: 'Atharraich an t-ùrlar',
    changeDrag: 'Atharraich an t-ùrlar (slaod airson gluasad)',
    choose: 'Tagh ùrlar',
    chooseYours: 'Tagh an ùrlar agad',
    chooseHint: 'Tagh sgeama dhathan a fhreagras ort',
    label: 'Ùrlar:',
    selectNamed: 'Tagh an ùrlar {name}',
    active: 'Gnìomhach',
  },
  bug: {
    floatingLabel: 'Aithris',
    floatingTitle: 'Aithris buga (slaod airson gluasad)',
    floatingAria: 'Aithris buga',
    title: 'Aithris buga',
    intro:
      'Cuidich sinn le leasachadh! Mìnich an duilgheadas. Cruinnichidh sinn am fiosrachadh teicnigeach gu fèin-obrachail airson an duilgheadas a lorg.',
    issueType: 'Seòrsa duilgheadais',
    typeBug: 'Buga / mearachd',
    typeUi: 'Duilgheadas taisbeanaidh',
    typePerformance: 'Duilgheadas dèanadais',
    typeFeature: 'Iarrtas gleus',
    typeOther: 'Eile',
    issueTitle: 'Tiotal',
    issueTitlePlaceholder: 'Mìneachadh goirid air an duilgheadas',
    description: 'Mìneachadh mionaideach',
    descriptionPlaceholder:
      'Mìnich dè thachair, dè bha dùil agad ris, agus na ceumannan airson an duilgheadas ath-chruthachadh...',
    anonymous: 'Cuir gun ainm',
    anonymousNoteOn:
      'Gun seòladh puist-d, chan urrainn dhuinn innse dhut nuair a bhios an duilgheadas air a chàradh no ceistean a chur ort.',
    anonymousNoteOff:
      'Thoir dhuinn an seòladh puist-d agad los gun urrainn dhuinn innse dhut mu chàradh agus ceistean a chur ma bhios feum. Bidh sinn a’ cleachdadh an aon dearbhaidh ’s a tha aig a’ chlàradh (fìor àrainn, gun seòlaidhean cuidhteis).',
    yourEmail: 'Am post-d agad',
    emailPlaceholder: 'an.seoladh@ball-sampaill.scot',
    currentUrl: 'URL na duilleige làithreach',
    openPopups: 'Uinneag(an) fosgailte nuair a chaidh seo aithris:',
    diagnostics: 'Fiosrachadh teicnigeach (air a chruinneachadh gu fèin-obrachail):',
    browser: 'Brabhsair',
    device: 'Uidheam',
    screen: 'Sgrìn',
    viewport: 'Port-seallaidh',
    loadingValue: 'A’ luchdadh...',
    consoleCaptured: 'Logaichean consoil air an cruinneachadh: {count} innteart',
    errorsCount: '{count} mearachd',
    warningsCount: '{count} rabhadh',
    infoCount: '{count} fiosrachadh',
    logsCount: '{count} log',
    noLogs:
      'Chan eil logaichean consoil ann fhathast. Bidh an siostam a’ clàradh a h-uile toradh bho thoiseach luchdaidh na duilleige.',
    submit: 'Cuir an aithisg',
    submitting: 'A’ cur...',
    errEmailRequired: 'Tha feum air seòladh puist-d mura cuir tu e gun ainm.',
    errEmailInvalid: 'Cuir a-steach seòladh puist-d dligheach.',
    toastSubmitting: 'A’ cur na h-aithisg...',
    toastSuccess: 'Chaidh an aithisg a chur. Mòran taing airson ar cuideachadh!',
    toastFailure:
      'Cha deach an aithisg a chur. Feuch ris a-rithist no sgrìobh gu markus.van.kempen@gmail.com',
  },
  contact: {
    openForm: 'Cuir teachdaireachd',
    title: 'Cuir fios thugainn',
    intro: 'Lìon am foirm gu h-ìosal agus freagraidh sinn cho luath ’s a ghabhas.',
    closeAria: 'Dùin am foirm fios',
    successTitle: 'Mòran taing! Chaidh an teachdaireachd agad a chur.',
    successText: 'Bidh sinn ann an conaltradh a dh’aithghearr.',
    sendAnother: 'Cuir teachdaireachd eile',
    name: 'Ainm',
    namePlaceholder: 'An ainm slàn agad',
    email: 'Post-d',
    emailPlaceholder: 'an.seoladh@ball-sampaill.scot',
    inquiryType: 'Seòrsa ceiste',
    inqGeneral: 'Ceist choitcheann',
    inqOrder: 'Ceist mu òrdugh',
    inqProduct: 'Fiosrachadh bathair',
    inqWholesale: 'Ceist mhòr-reic',
    inqTechnical: 'Taic theicnigeach',
    inqFeedback: 'Beachdan',
    inqOther: 'Eile',
    subject: 'Cuspair',
    subjectPlaceholder: 'Mìneachadh goirid air a’ cheist agad',
    message: 'Teachdaireachd',
    messagePlaceholder: 'Thoir dhuinn barrachd mu do cheist no do bheachdan...',
    send: 'Cuir an teachdaireachd',
    errorPrefix: 'Mearachd:',
    errSendFailed: 'Cha deach an teachdaireachd a chur. Feuch ris a-rithist.',
    directEmail: 'Post-d dìreach:',
    responseTime: 'Ùine freagairt:',
    responseTimeValue: 'Mar as trice am broinn 24 uair',
  },
  page: {
    loading: 'A’ luchdadh na duilleige...',
    notFoundTitle: 'Cha deach an duilleag a lorg',
    notFoundText: 'Chan eil an duilleag a tha thu a’ sireadh ann no chaidh a toirt air falbh.',
    edit: 'Deasaich an duilleag',
    editTitle: 'Deasaich an duilleag seo',
    haveQuestions: 'A bheil ceist agad?',
    legalQuestions: 'Ma tha ceist agad, cuir fios thugainn gun teagamh.',
    contactUs: 'Cuir fios thugainn',
    emailLabel: 'Post-d:',
    phoneLabel: 'Fòn:',
  },
  cookies: {
    aria: 'Roghainnean briosgaidean',
    bannerLead: 'Bidh sinn a’ cleachdadh briosgaidean',
    bannerRest:
      'gus an t-eòlas agad a leasachadh, cleachdadh na làraich a sgrùdadh, agus taic a thoirt dhan mhargaidheachd againn. Faodaidh tu iad uile a ghabhail, feadhainn nach eil riatanach a dhiùltadh, no na roghainnean agad a ghnàthachadh.',
    acceptAll: 'Gabh ris na h-uile',
    rejectNonEssential: 'Diùlt iad nach eil riatanach',
    customize: 'Gnàthaich',
    title: 'Roghainnean briosgaidean',
    necessary: 'Riatanach',
    necessaryAlways: '(an-còmhnaidh air)',
    necessaryDesc: 'A dhìth airson bun-ghnìomhan na làraich.',
    analytics: 'Briosgaidean anailis',
    analyticsDesc: 'Cuidichidh iad sinn tuigsinn mar a chleachdas luchd-tadhail an làrach.',
    marketing: 'Briosgaidean margaidheachd',
    marketingDesc: 'Air an cleachdadh gus sanasan iomchaidh a lìbhrigeadh agus an èifeachd a thomhas.',
    save: 'Sàbhail na roghainnean',
    back: 'Air ais',
  },
  abcTool: {
    seoTitle: 'Tionndaidhear MIDI gu ABC | Inneal ABC an-asgaidh',
    seoDescription:
      'Tionndaidh faidhlichean MIDI gu nodaireachd ABC air-loidhne. Luchdaich, cluich, deasaich agus luchdaich a-nuas puirt fhìdhle Cheilteach ann an ABC.',
    iframeTitle: 'Inneal ABC — tionndaidhear MIDI gu ABC',
    loading: 'A’ luchdadh an inneil ABC...',
    loadError: 'Cha b’ urrainn an t-inneal ABC a luchdadh. Ùraich an duilleag.',
    reload: 'Ath-luchdaich an duilleag',
    pageTitle: 'Tionndaidhear MIDI gu ABC',
    title: 'Tionndaidhear MIDI gu ABC',
    subtitle: 'Seall, cluich agus tionndaidh puirt fhìdhle — a’ tòiseachadh le BarFly',
    jumpAria: 'Leum gu earrannan',
    jumpTo: 'Leum gu :',
    jumpAbc: 'Teacsa ABC',
    jumpControls: 'Stiùirichean',
    jumpStatus: 'Inbhe',
    jumpNotation: 'Nodaireachd chlasaigeach',
    howTo: 'Mar a chleachdas tu e',
    howPlay: '<strong>Cluich:</strong> cleachd na stiùirichean (air an làimh dheis) airson cluich, luas agus ionnsramaid',
    howEdit: '<strong>Deasaich:</strong> atharraich an teacsa ABC (air an làimh chlì), an uair sin ùraich an nodaireachd',
    howUpload: '<strong>Luchdaich suas:</strong> cleachd Luchdaich suas anns na stiùirichean airson .mid / .midi / .abc',
    howDownload: '<strong>Luchdaich a-nuas:</strong> PDF, ìomhaigh, MIDI, no lethbhreac/sàbhail ABC',
    howLogin:
      '<strong>Nòta:</strong> <a href="/login">log a-steach</a> (no clàraich) airson a h-uile gnìomh — feumaidh cuid de luchdachaidhean cunntas.',
    abcText: 'Teacsa ABC',
    abcPlaceholder: 'Nochdaidh nodaireachd ABC an seo... Faodaidh tu teacsa ABC a chur a-steach cuideachd.',
    updateNotation: '🔄 Ùraich an nodaireachd bhon teacsa ABC',
    controls: 'Stiùirichean',
    tempo: 'Luaths (BPM):',
    instrument: 'Ionnsramaid:',
    pianoStandard: 'Piàna — àbhaisteach (FluidR3_GM)',
    pianoHq: 'Piàna — grand HQ (MusyngKite)',
    pianoSimple: 'Piàna — synth sìmplidh',
    play: '▶ Cluich',
    stop: '⏹ Stad',
    copyAbc: '📋 Leth-bhreac ABC',
    saveAbc: '💾 Sàbhail ABC',
    pdf: '📄 PDF',
    image: '🖼️ Ìomhaigh',
    midi: '🎵 MIDI',
    authMessage:
      '<a href="/login">Log a-steach</a> no clàraich gus na gnìomhan liatha a chleachdadh.',
    uploadLabel: '📁 Luchdaich suas faidhle MIDI no ABC',
    uploadHint: 'Luchdaich suas .mid / .midi / .abc an àite an eisimpleir, no cuir a-steach fo Teacsa ABC.',
    status: 'Inbhe',
    statusLoadingExample: 'A’ luchdadh port eisimpleir…',
    notation: 'Nodaireachd chlasaigeach',
    notationPlaceholder: 'Nochdaidh an nodaireachd an seo às dèidh an tionndaidh',
    loginToUse: 'Log a-steach no clàraich gus an gnìomh seo a chleachdadh.',
    loginToUseFeature: 'Log a-steach no clàraich gus {feature} a chleachdadh.',
    featSaveAbc: 'Sàbhail ABC',
    featPdf: 'Luchdaich a-nuas PDF',
    featImage: 'Luchdaich a-nuas ìomhaigh',
    featMidi: 'Luchdaich a-nuas MIDI',
    errLibraries: 'Mearachd: chan eil na leabhar-lannan riatanach ri fhaighinn. Ùraich an duilleag.',
    errNoAbc: 'Chan eil teacsa ABC ri thaisbeanadh. Cuir a-steach no luchdaich suas nodaireachd ABC.',
    rendering: 'A’ dèanamh nodaireachd bhon teacsa ABC...',
    notationUpdated: '✓ Nodaireachd ùraichte!',
    warnSyntax: 'Rabhadh: cha b’ urrainn an nodaireachd a dhèanamh. Thoir sùil air co-chòrdadh ABC.',
    loadingBarfly: 'A’ luchdadh port eisimpleir: BarFly...',
    dropFile: 'Leig sìos faidhle MIDI (.mid, .midi) no ABC (.abc)',
    selectFile: 'Tagh faidhle MIDI no ABC',
    loadingAbc: 'A’ luchdadh faidhle ABC...',
    errConversionLibs: 'Mearachd: cha deach na leabhar-lannan tionndaidh a luchdadh. Ùraich an duilleag.',
    errConvertMidi: 'Mearachd a’ tionndadh an fhaidhle MIDI. Thoir sùil air a’ chonsol.',
    loadingInstrument: 'A’ luchdadh {name} ({font})...',
    noNotationPlay: 'Chan eil nodaireachd ri chluich. Luchdaich faidhle an toiseach.',
    playing: 'A’ cluich...',
    stopped: 'Air stad.',
    errMidiPlayer: 'Mearachd: cha deach MidiPlayer a luchdadh.',
    playbackFinished: 'Crìochnaich an cluich.',
    copied: '✓ Air a lethbhreacadh dhan stòr-bhòrd!',
    saved: '✓ Air a shàbhaladh!',
    noAbcSave: 'Chan eil nodaireachd ABC ri shàbhaladh.',
    noNotationDownload: 'Chan eil nodaireachd ri luchdadh a-nuas.',
  },
  player: {
    title: 'Cluicheadair Cranford Pub',
    openHint: 'Slaod airson gluasad • Briog airson fosgladh',
    sampleHint: 'Slaod airson gluasad • Briog airson sampall a chluinntinn',
    signInHint: 'Log a-steach airson puirt Cheap Bhreatainn a chluinntinn',
    sampleBadge: 'Sampall',
    guestBanner: 'Seo sampall. Clàraich an-asgaidh gus {count} port eile à Ceap Bhreatainn a chluinntinn.',
    guestCtaRegister: 'Cruthaich cunntas',
    guestCtaSignIn: 'Log a-steach',
    guestCtaVerify: 'Dearbh am post-d',
    guestLockedHint: 'Clàraich airson an liosta slàn',
    verifyEmail:
      'Dearbh am post-d agad airson an cluicheadair a chleachdadh. Thoir sùil air a’ bhogsa a-steach agad no fosgail a’ phròifil agad airson an ceangal dearbhaidh a chur a-rithist.',
    dragToMove: 'Slaod airson gluasad',
    offlineBadge: 'Far loidhne · {count} port san tasgadan',
    offlineTooltip:
      'Tha {count} faidhle MP3 air an tasgadh às dèidh tadhal air-loidhne (stàladh no ùrachadh na h-aplacaid).',
    fullscreen: 'Làn-sgrìn',
    exitFullscreen: 'Fàg an làn-sgrìn',
    minimize: 'Lùghdaich',
    tunePosition: 'Port {current} de {total}',
    selectAria: 'Port: {title}, traca {current} de {total}. Fosgail an liosta airson fear eile a thaghadh.',
    seekHint: 'Thoir gnogadh no briog airson sireadh',
    previous: 'Air ais',
    play: 'Cluich',
    pause: 'Cuir na stad',
    next: 'Air adhart',
    shuffle: 'Air thuaiream',
    random: 'Cluich port air thuaiream',
    volumeDown: 'Nas sàmhaiche',
    volumeUp: 'Nas àirde',
    volumeLabel: 'Àirde {level} de 10',
  },
  nudge: {
    title: 'Fuasgail an cluicheadair ciùil!',
    body: 'Faigh an cluicheadair eadar-ghnìomhach againn le stiùireadh cluich',
    cta: 'Cruthaich cunntas',
  },
};

const frCA = french({
  emailNoun: 'courriel',
  emailAdj: 'courriel',
  emailLabel: 'Courriel',
  emailYours: 'Votre courriel',
  contactTitle: 'Nous joindre',
});

const frFR = french({
  emailNoun: 'e-mail',
  emailAdj: 'e-mail',
  emailLabel: 'E-mail',
  emailYours: 'Votre e-mail',
  contactTitle: 'Contactez-nous',
});

/** British English keeps the American "color" out of user-facing copy. */
const enGB = {
  ...en,
  theme: {
    ...en.theme,
    floatingLabel: 'Colour',
    chooseHint: 'Select a colour theme that matches your style',
  },
};

export const UI_MESSAGES = {
  en,
  'en-US': en,
  'en-GB': enGB,
  'fr-CA': frCA,
  'fr-FR': frFR,
  de,
  ga,
  gd,
};
